import { ArrowCounterClockwiseIcon, XIcon } from '@phosphor-icons/react'
import React from 'react'

import { createTrimOnBlurHandler, mergeRefs } from '@/utils/dom'

import { Icon } from '../icon'
import { Label } from '../label'
import * as S from './text-input.styled'
import { TextInputProps } from './text-input.types'

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      label,
      labelPosition = 'top',
      tooltip,
      helperText,
      error,
      icon,
      leadingSlot,
      characterLimit,
      name,
      id,
      clearable,
      onRestore,
      trailingAction,
      className,
      size = 'medium',
      value,
      disabled,
      readOnly,
      required,
      withReservedFooter,
      inputWrapperRef,
      dataComponentId = 'TextInput',
      onChange,
      onBlur,
      ...props
    },
    ref,
  ) => {
    const valueLength = value == null ? 0 : String(value).length
    const hasValue = valueLength > 0
    const hasLimit = characterLimit != null
    const isOverLimit = hasLimit && valueLength > characterLimit
    // Note: exceeding the soft character limit is itself an error state.
    const hasError = !!error || isOverLimit

    // Note: read-only and disabled fields are static — no clear/restore affordances.
    const isInteractive = !disabled && !readOnly
    const showClear = !!clearable && hasValue && isInteractive
    const showRestore = !!onRestore && isInteractive
    // Note: a read-only field keeps its trailing action (e.g. the show/hide toggle of a saved secret)
    const showTrailingAction = !!trailingAction && !disabled
    const showActions = showClear || showRestore || showTrailingAction

    const message = error ?? helperText
    const shouldShowFooter = withReservedFooter || !!message || hasLimit
    const inputId = id ?? name
    // Note: the input box is itself a <label> (see InputWrapper), so without an explicit reference the
    // Clear/Restore button names leak into the input's accessible name.
    const labelId = label ? `${inputId}-label` : undefined
    const helperId = message ? `${inputId}-message` : undefined
    const counterId = hasLimit ? `${inputId}-counter` : undefined
    const describedBy = [helperId, counterId].filter(Boolean).join(' ') || undefined

    const localRef = React.useRef<HTMLInputElement>(null)

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      onChange?.({
        ...event,
        target: { name: name ?? '', value: '' } as HTMLInputElement,
      } as unknown as React.ChangeEvent<HTMLInputElement>)
      localRef.current?.focus()
    }

    const handleTrailingAction = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      trailingAction?.onClick()
      localRef.current?.focus()
    }

    const handleRestore = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault()
      event.stopPropagation()
      onRestore?.()
      localRef.current?.focus()
    }

    const handleBlur = createTrimOnBlurHandler<HTMLInputElement>((e) => {
      if (e.target.value !== String(value ?? '')) {
        onChange?.(e as React.ChangeEvent<HTMLInputElement>)
      }
      onBlur?.(e)
    })

    return (
      <S.Container
        className={className}
        $labelPosition={labelPosition}
        $size={size}
        data-component-id={dataComponentId}
      >
        {label && (
          <Label
            htmlFor={inputId}
            id={labelId}
            size={size}
            disabled={disabled}
            hasError={hasError}
            tooltip={tooltip}
            required={required}
          >
            {label}
          </Label>
        )}
        <S.Field $size={size}>
          <S.InputWrapper
            ref={inputWrapperRef}
            $size={size}
            $hasError={hasError}
            $isDisabled={!!disabled}
            $isReadOnly={!!readOnly}
          >
            {(leadingSlot || icon) && (
              <S.LeadingSlot
                $size={size}
                $hasError={hasError}
                $isDisabled={!!disabled}
                $isReadOnly={!!readOnly}
                data-component-id="LeadingSlot"
              >
                {leadingSlot ?? (icon && <Icon icon={icon} />)}
                <S.Divider $hasError={hasError} $isDisabled={!!disabled} $isReadOnly={!!readOnly} />
              </S.LeadingSlot>
            )}
            <S.Input
              ref={mergeRefs(localRef, ref)}
              $hasError={hasError}
              $size={size}
              id={inputId}
              name={name}
              value={value}
              disabled={disabled}
              readOnly={readOnly}
              required={required}
              aria-invalid={hasError || undefined}
              aria-describedby={describedBy}
              aria-labelledby={labelId}
              onChange={onChange}
              onBlur={handleBlur}
              {...props}
            />
            {showActions && (
              <S.ActionsWrapper>
                {showClear && (
                  <S.InputActionButton
                    $size={size}
                    use={hasError ? 'ghost-error' : 'ghost'}
                    size="unset"
                    aria-label="Clear"
                    onClick={handleClear}
                  >
                    <Icon icon={XIcon} />
                  </S.InputActionButton>
                )}
                {showRestore && (
                  <S.InputActionButton
                    $size={size}
                    use={hasError ? 'ghost-error' : 'ghost'}
                    size="unset"
                    aria-label="Restore"
                    onClick={handleRestore}
                  >
                    <Icon icon={ArrowCounterClockwiseIcon} />
                  </S.InputActionButton>
                )}
                {showTrailingAction && trailingAction && (
                  <S.InputActionButton
                    $size={size}
                    use={hasError ? 'ghost-error' : 'ghost'}
                    size="unset"
                    aria-label={trailingAction.label}
                    onClick={handleTrailingAction}
                  >
                    <Icon icon={trailingAction.icon} />
                  </S.InputActionButton>
                )}
              </S.ActionsWrapper>
            )}
          </S.InputWrapper>
          {shouldShowFooter && (
            <S.Footer $size={size}>
              {message && (
                <S.Message id={helperId} $hasError={hasError} role={error ? 'alert' : undefined}>
                  {message}
                </S.Message>
              )}
              {hasLimit && (
                <S.Counter id={counterId} $isOverLimit={isOverLimit}>
                  <S.CurrentCount $digits={String(characterLimit).length}>
                    {valueLength}
                  </S.CurrentCount>
                  /{characterLimit}
                </S.Counter>
              )}
            </S.Footer>
          )}
        </S.Field>
      </S.Container>
    )
  },
)
